/**
 * Importing history from the previous Reta and its side files.
 *
 * Sources this understands (auto-detected from content):
 *   old-state     the previous app's localStorage, either live on the same
 *                 origin, as its "Export All Data" backup
 *                 ({app:'Reta v2.0', data:{reta_*}}), or as the server mirror
 *                 ({state:{reta_*}}) from data/user_state/ben.json
 *   inbox-jsonl   the old server's dispatch inbox (food / weight / workout)
 *   oura-ledger   oura-ledger.jsonl from the Mac pipeline
 *   apple-health  data/apple_health.jsonl from the old server
 *   apple-workouts data/apple_workouts.jsonl
 *   renpho        renpho.json
 *   reta-export   this app's own backup (handed to the normal restore)
 *
 * Rules:
 *   - Every record gets a DETERMINISTIC id derived from its source, so a
 *     re-import updates rather than duplicates.
 *   - Original timestamps are preserved. A source that has only a calendar
 *     date is stamped at local noon on that date, never "now".
 *   - Nothing is silently dropped: whatever is not carried over is listed in
 *     the inventory with the reason.
 */
import { newId } from '../lib/id';
import { mergeDay } from './biometrics';
import { get, getAll, put, putMany, update } from './db';
import { parseHaeDate } from './applehealth';
import { parseLedgerRow } from './oura';
import { loadSettings, saveSettings, validateExport } from './export';
import type { CheckIn, Compound, DailyMetrics, DoseLog, FoodItem, FoodLog, GiTag, LoggedFood, MealSlot, Protocol, Reading, Vial, WorkoutSession } from './schema';
import { GI_TAGS } from './schema';

const LB_PER_KG = 2.20462262185;
const lbToKg = (lb: number) => Math.round((lb / LB_PER_KG) * 1000) / 1000;

/** Local noon on a calendar date, as an instant. Used when a source has only a date. */
export function noonOf(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12, 0, 0).toISOString();
}

function localDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

// --------------------------------------------------------------------------
// Detection
// --------------------------------------------------------------------------

export type LegacyKind =
  | 'old-state' | 'inbox-jsonl' | 'oura-ledger' | 'apple-health' | 'apple-workouts' | 'renpho' | 'reta-export' | 'unknown';

export interface Detected { kind: LegacyKind; data: unknown }

/** Work out what a file is from its content alone. */
export function detectLegacy(text: string): Detected {
  const trimmed = text.trim();
  if (!trimmed) return { kind: 'unknown', data: null };

  // JSON Lines: several lines that each parse as an object.
  const lines = trimmed.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length > 1 && lines.every((l) => l.startsWith('{'))) {
    const rows: Record<string, unknown>[] = [];
    for (const l of lines) { try { rows.push(JSON.parse(l)); } catch { /* skip malformed */ } }
    if (!rows.length) return { kind: 'unknown', data: null };
    const first = rows[0]!;
    if ('type' in first && 'received_at' in first) return { kind: 'inbox-jsonl', data: rows };
    if ('pulled_at' in first || 'sleep_score' in first || 'readiness_score' in first) return { kind: 'oura-ledger', data: rows };
    if ('duration_sec' in first && 'name' in first) return { kind: 'apple-workouts', data: rows };
    if ('weight_lbs' in first || 'weight_kg' in first || 'resting_hr' in first) return { kind: 'apple-health', data: rows };
    return { kind: 'unknown', data: rows };
  }

  let obj: unknown;
  try { obj = JSON.parse(trimmed); } catch { return { kind: 'unknown', data: null }; }
  if (!obj || typeof obj !== 'object') return { kind: 'unknown', data: obj };
  const o = obj as Record<string, unknown>;
  // A one-line .jsonl parses as a single object; recognise the row shapes.
  if (!Array.isArray(obj)) {
    if ('type' in o && 'received_at' in o) return { kind: 'inbox-jsonl', data: [o] };
    if ('pulled_at' in o && 'day' in o) return { kind: 'oura-ledger', data: [o] };
    if ('duration_sec' in o && 'name' in o && 'day' in o) return { kind: 'apple-workouts', data: [o] };
    if ('day' in o && ('weight_lbs' in o || 'weight_kg' in o) && !('readings' in o)) return { kind: 'apple-health', data: [o] };
  }
  if (o.format === 'reta-export') return { kind: 'reta-export', data: obj };
  if (Array.isArray(o.readings) && 'generated' in o) return { kind: 'renpho', data: obj };
  if (o.data && typeof o.data === 'object' && Object.keys(o.data as object).some((k) => k.startsWith('reta_'))) {
    return { kind: 'old-state', data: o.data };
  }
  if (o.state && typeof o.state === 'object' && Object.keys(o.state as object).some((k) => k.startsWith('reta_'))) {
    return { kind: 'old-state', data: o.state };
  }
  if (Object.keys(o).some((k) => k.startsWith('reta_'))) return { kind: 'old-state', data: obj };
  return { kind: 'unknown', data: obj };
}

/** Read the previous app's keys straight out of this origin's localStorage. */
export function readLegacyLocalStorage(): Record<string, unknown> | null {
  try {
    const out: Record<string, unknown> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('reta_')) continue;
      const raw = localStorage.getItem(k);
      if (raw === null) continue;
      try { out[k] = JSON.parse(raw); } catch { out[k] = raw; }
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

// --------------------------------------------------------------------------
// Mapping
// --------------------------------------------------------------------------

export interface LegacyRecords {
  metrics: Map<string, Record<string, Reading>>;
  checkins: CheckIn[];
  foodLogs: FoodLog[];
  foods: FoodItem[];
  doses: DoseLog[];
  vials: Vial[];
  sessions: WorkoutSession[];
  goals?: { kcal?: number; protein?: number; carbs?: number; fat?: number; steps?: number; weightKg?: number };
  profile?: NonNullable<import('./schema').Settings['profile']>;
  /** Compound started-on dates learned from the source. */
  startedOn: Record<string, string>;
}

export interface Inventory {
  kind: LegacyKind;
  counts: Record<string, number>;
  /** What was seen but not carried over, and why. */
  skipped: { what: string; why: string; count?: number }[];
  warnings: string[];
}

export interface LegacyResult { records: LegacyRecords; inventory: Inventory }

function empty(kind: LegacyKind): LegacyResult {
  return {
    records: { metrics: new Map(), checkins: [], foodLogs: [], foods: [], doses: [], vials: [], sessions: [], startedOn: {} },
    inventory: { kind, counts: {}, skipped: [], warnings: [] },
  };
}

function setMetric(r: LegacyRecords, day: string, field: string, value: number, source: Reading['source'], at: string) {
  const bucket = r.metrics.get(day) ?? {};
  bucket[field] = { value, source, at };
  r.metrics.set(day, bucket);
}

const OLD_SLOTS: Record<string, MealSlot> = {
  breakfast: 'breakfast', lunch: 'lunch', dinner: 'dinner', snack: 'snack',
  'post-workout': 'snack', 'pre-workout': 'snack', supper: 'dinner', brunch: 'breakfast',
};

/** "4oz" / "0.5 cup" / "1 scoop" -> qty and unit; anything else keeps the text as the unit. */
function splitAmount(amount: string | undefined): { qty: number; unit: string } {
  const a = (amount ?? '').trim();
  const m = /^(\d+(?:\.\d+)?)\s*([a-zA-Z ()~]+)?$/.exec(a);
  if (m) return { qty: Number(m[1]), unit: (m[2] ?? '').trim() || 'serving' };
  return { qty: 1, unit: a || 'serving' };
}

function oldFoodItem(f: Record<string, unknown>): LoggedFood {
  const { qty, unit } = splitAmount(typeof f.amount === 'string' ? f.amount : undefined);
  return {
    name: String(f.food ?? f.name ?? 'Food'),
    qty, unit,
    macros: {
      kcal: isNum(f.cal) ? f.cal : 0,
      protein: isNum(f.p) ? f.p : isNum(f.protein_g) ? f.protein_g : 0,
      carbs: isNum(f.c) ? f.c : isNum(f.carbs_g) ? f.carbs_g : 0,
      fat: isNum(f.f) ? f.f : isNum(f.fat_g) ? f.fat_g : 0,
    },
  };
}

/** The previous app's localStorage (any of its three wrappers). */
export function mapOldState(state: Record<string, unknown>, compoundId = 'reta'): LegacyResult {
  const res = empty('old-state');
  const r = res.records;
  const inv = res.inventory;
  const count = (k: string, n = 1) => { inv.counts[k] = (inv.counts[k] ?? 0) + n; };

  // Pins -> doses
  const pins = Array.isArray(state.reta_pins_v1) ? (state.reta_pins_v1 as Record<string, unknown>[]) : [];
  for (const p of pins) {
    const day = typeof p.date === 'string' ? p.date : null;
    if (!day || !isNum(p.dose_mg)) { inv.skipped.push({ what: 'pin', why: `missing date or dose: ${JSON.stringify(p).slice(0, 80)}` }); continue; }
    const effects = p.effects && typeof p.effects === 'object' ? p.effects as Record<string, unknown> : {};
    const noteParts = [
      typeof p.notes === 'string' && p.notes ? p.notes : '',
      typeof p.side_effects === 'string' && p.side_effects && p.side_effects !== 'none' ? `side effects: ${p.side_effects}` : '',
      Object.entries(effects).filter(([, v]) => isNum(v) && v > 0).map(([k, v]) => `${k} ${v}/10`).join(', '),
      typeof p.cart === 'string' ? `cart ${p.cart}` : '',
    ].filter(Boolean);
    const dose: DoseLog = {
      id: `legacy-pin-${day}-${p.dose_mg}-${isNum(p.units) ? p.units : 'x'}`,
      compoundId, day, at: noonOf(day), doseMg: p.dose_mg, route: 'subcutaneous',
      // Pen-era pins record units 0 when the cartridge strength was never
      // set: that means "unknown", not "zero units", so it is not carried.
      ...(isNum(p.units) && p.units > 0 ? { units: p.units } : {}),
      ...(typeof p.site === 'string' && p.site ? { site: p.site } : {}),
      ...(noteParts.length ? { note: noteParts.join(' · ') } : {}),
    };
    r.doses.push(dose); count('doses');
  }

  // Weigh-ins -> metrics
  const weights = Array.isArray(state.reta_weights_v1) ? (state.reta_weights_v1 as Record<string, unknown>[]) : [];
  for (const w of weights) {
    if (typeof w.date !== 'string' || !isNum(w.weight)) { inv.skipped.push({ what: 'weigh-in', why: 'missing date or weight' }); continue; }
    const source: Reading['source'] = w.src === 'apple_health' ? 'apple_health' : 'manual';
    setMetric(r, w.date, 'weightKg', lbToKg(w.weight), source, noonOf(w.date)); count('weights');
  }

  // Days -> food logs, water and steps
  const days = state.reta_days_v1 && typeof state.reta_days_v1 === 'object' ? state.reta_days_v1 as Record<string, Record<string, unknown>> : {};
  for (const [day, d] of Object.entries(days)) {
    const meals = d.meals && typeof d.meals === 'object' ? d.meals as Record<string, Record<string, unknown>[]> : {};
    for (const [slotName, items] of Object.entries(meals)) {
      if (!Array.isArray(items) || !items.length) continue;
      const slot = OLD_SLOTS[slotName.toLowerCase()];
      if (!slot) inv.warnings.push(`Meal slot "${slotName}" on ${day} mapped to snack.`);
      r.foodLogs.push({
        id: `legacy-meal-${day}-${slug(slotName)}`,
        day, at: noonOf(day), slot: slot ?? 'snack',
        items: items.map(oldFoodItem),
        note: slot ? undefined : `previously "${slotName}"`,
      }); count('meals');
    }
    if (isNum(d.steps) && d.steps > 0) { setMetric(r, day, 'steps', d.steps, 'manual', noonOf(day)); count('steps'); }
    if (isNum(d.water_oz) && d.water_oz > 0) inv.skipped.push({ what: `water ${d.water_oz} oz on ${day}`, why: 'the new app does not track water' });
  }

  // Check-ins
  const checkins = state.reta_checkin_v1 && typeof state.reta_checkin_v1 === 'object' ? state.reta_checkin_v1 as Record<string, Record<string, unknown>> : {};
  for (const [day, c] of Object.entries(checkins)) {
    const row: CheckIn = { day, updatedAt: noonOf(day) };
    // The previous app's GI scale ran the other way ("0 settled · 10 rough");
    // this app's reads "0 awful · 10 settled", like every other check-in field.
    // Invert on the way in, or the history charts upside down.
    if (isNum(c.gi)) row.gi = 10 - Math.max(0, Math.min(10, c.gi));
    if (isNum(c.energy)) row.energy = c.energy;
    if (isNum(c.hunger)) row.hunger = c.hunger;
    if (isNum(c.cravings)) row.cravings = c.cravings;
    if (isNum(c.sleep)) row.sleepQuality = c.sleep;
    if (isNum(c.mood)) row.mood = c.mood;
    if (Array.isArray(c.tags)) row.giTags = (c.tags as unknown[]).filter((t): t is GiTag => (GI_TAGS as readonly string[]).includes(String(t)));
    if (isNum(c.weight)) setMetric(r, day, 'weightKg', lbToKg(c.weight), 'manual', noonOf(day));
    if (Object.keys(row).length > 2) { r.checkins.push(row); count('checkins'); }
  }

  // Cartridges -> vials
  const carts = Array.isArray(state.reta_carts_v1) ? (state.reta_carts_v1 as Record<string, unknown>[]) : [];
  for (const c of carts) {
    if (!isNum(c.mg) || c.mg <= 0) { inv.skipped.push({ what: `cartridge ${String(c.id)}`, why: 'strength (mg) was never set' }); continue; }
    const status: Vial['status'] = c.status === 'active' ? 'active' : c.status === 'spent' ? 'spent' : 'sealed';
    r.vials.push({
      id: `legacy-cart-${String(c.id)}`, compoundId, label: `Cart ${String(c.id)}`,
      contentMg: c.mg, diluentMl: isNum(c.ml) ? c.ml : null,
      ...(typeof c.date === 'string' && c.date ? { openedAt: noonOf(c.date), reconstitutedAt: noonOf(c.date) } : {}),
      ...(isNum(c.prime) && c.prime > 0 ? { primeLossMl: c.prime } : {}),
      storage: c.status === 'frozen' ? 'freezer' : 'fridge', status, createdAt: noonOf(typeof c.date === 'string' && c.date ? c.date : '2026-08-22'),
    }); count('vials');
  }

  // Oura cache (14 days the old app kept) -> metrics
  const oc = (state.reta_oura_cache as { days?: Record<string, unknown>[] } | undefined)?.days;
  for (const d of Array.isArray(oc) ? oc : []) {
    if (typeof d.day !== 'string') continue;
    const at = noonOf(d.day);
    const m: [string, unknown][] = [
      ['sleepScore', d.sleep], ['readinessScore', d.readiness], ['hrvMs', d.hrv], ['restingHr', d.rhr],
      ['tempDeviationC', d.temp], ['activityScore', d.activity], ['steps', d.steps], ['activeKcal', d.active_cal],
    ];
    for (const [field, v] of m) if (isNum(v)) setMetric(r, d.day, field, v, 'oura', at);
    if (isNum(d.sleep_h)) setMetric(r, d.day, 'totalSleepSec', Math.round(d.sleep_h * 3600), 'oura', at);
    count('oura-days');
  }

  // Health cache -> metrics (same rows as apple_health.jsonl; harmless overlap)
  const hc = (state.reta_health_cache as { days?: Record<string, unknown>[] } | undefined)?.days;
  for (const d of Array.isArray(hc) ? hc : []) mapHealthRow(d, r, inv);

  // Renpho cache
  const rc = (state.reta_renpho_cache as { readings?: Record<string, unknown>[] } | undefined)?.readings;
  for (const d of Array.isArray(rc) ? rc : []) mapRenphoReading(d, r, inv);

  // Workouts cache
  const wc = (state.reta_workouts_cache as { workouts?: Record<string, unknown>[] } | undefined)?.workouts;
  for (const w of Array.isArray(wc) ? wc : []) mapAppleWorkout(w, r, inv);

  // Templates, recents, my foods -> library items so the frequent chips work
  const lib: [string, unknown][] = [['template', state.reta_meal_templates_v1], ['recent', state.reta_recents_v1], ['myfood', state.reta_myfoods_v1], ['favourite', state.reta_favs_v1]];
  for (const [kind, list] of lib) {
    for (const f of Array.isArray(list) ? (list as Record<string, unknown>[]) : []) {
      const name = String(f.name ?? f.food ?? '').trim();
      if (!name || !isNum(f.cal)) continue;
      const per100 = f.per100 === true;
      r.foods.push({
        id: `legacy-food-${slug(name)}`, name,
        macros: { kcal: f.cal, protein: isNum(f.p) ? f.p : 0, carbs: isNum(f.c) ? f.c : 0, fat: isNum(f.f) ? f.f : 0 },
        refQty: per100 ? 100 : 1, refUnit: per100 ? 'g' : 'serving',
        source: 'custom', useCount: 1, createdAt: noonOf('2026-08-01'),
      }); count(`foods-${kind}`);
    }
  }

  // Targets -> goals
  const t = state.reta_targets_v1 as Record<string, unknown> | undefined;
  if (t && typeof t === 'object') {
    r.goals = {
      ...(isNum(t.calories) ? { kcal: t.calories } : {}),
      ...(isNum(t.protein_g) ? { protein: t.protein_g } : {}),
      ...(isNum(t.carbs_g) ? { carbs: t.carbs_g } : {}),
      ...(isNum(t.fat_g) ? { fat: t.fat_g } : {}),
      ...(isNum(t.steps) ? { steps: t.steps } : {}),
    };
    if (isNum(t.water_oz)) inv.skipped.push({ what: `water target ${t.water_oz} oz`, why: 'the new app does not track water' });
    count('goals');
  }

  // Profile
  const pr = state.reta_profile_v1 as Record<string, unknown> | undefined;
  if (pr && typeof pr === 'object') {
    r.profile = {
      ...(typeof pr.name === 'string' ? { name: pr.name } : {}),
      ...(isNum(pr.start_weight_lbs) ? { startWeightKg: lbToKg(pr.start_weight_lbs) } : {}),
      ...(typeof pr.start_date === 'string' ? { startedOn: pr.start_date } : {}),
      ...(Array.isArray(pr.allergies) ? { allergies: (pr.allergies as unknown[]).map(String) } : {}),
      ...(typeof pr.eat_start === 'string' && typeof pr.eat_end === 'string' ? { eatingWindow: { start: pr.eat_start, end: pr.eat_end } } : {}),
      ...(typeof pr.timezone === 'string' ? { timezone: pr.timezone } : {}),
    };
    if (isNum(pr.goal_weight_lbs)) r.goals = { ...(r.goals ?? {}), weightKg: lbToKg(pr.goal_weight_lbs) };
    if (typeof pr.start_date === 'string') r.startedOn[compoundId] = pr.start_date;
    count('profile');
  }

  // Things deliberately not carried, named so nothing vanishes quietly.
  const ignore: Record<string, string> = {
    reta_grocery_v1: 'shopping list; not a health record',
    reta_milestones_v1: 'badges the old app awarded; derivable',
    reta_auth_v1: 'old app PIN/passkey; not applicable',
    reta_synced_inbox_ids: 'sync bookkeeping',
    reta_pill_hint_v1: 'UI state', reta_glance_hint: 'UI state', reta_v2_onb: 'UI state', reta_v3_onb: 'UI state',
    reta_seeded_v1: 'UI state', reta_seed_wk1fix_v1: 'UI state', reta_seed_20260803_v1: 'UI state', reta_foodfix_20260803_v1: 'UI state',
    reta_state_rev: 'sync bookkeeping', reta_last_sync: 'sync bookkeeping', reta_wlog_v1: 'empty in every copy seen',
    reta_oura_today_cache: 'partial-day snapshot superseded by the daily rows', reta_photo_queue_v1: 'unprocessed meal photos; images are not imported',
    reta_measure_v1: 'body measurements: not yet modelled in the new app', reta_comp_v1: 'composition entries: covered by the scale/Health rows',
  };
  for (const k of Object.keys(state)) {
    if (k in ignore) inv.skipped.push({ what: k, why: ignore[k]! });
    else if (!['reta_pins_v1', 'reta_weights_v1', 'reta_days_v1', 'reta_checkin_v1', 'reta_carts_v1', 'reta_oura_cache', 'reta_health_cache', 'reta_renpho_cache', 'reta_workouts_cache', 'reta_meal_templates_v1', 'reta_recents_v1', 'reta_myfoods_v1', 'reta_favs_v1', 'reta_targets_v1', 'reta_profile_v1'].includes(k)) {
      inv.skipped.push({ what: k, why: 'unrecognised key; kept in the source file, not imported' });
    }
  }
  return res;
}

function mapHealthRow(d: Record<string, unknown>, r: LegacyRecords, inv: Inventory) {
  if (typeof d.day !== 'string') { inv.skipped.push({ what: 'health row', why: 'no day' }); return; }
  const at = typeof d.updated_at === 'string' ? (parseHaeDate(d.updated_at)?.toISOString() ?? noonOf(d.day)) : noonOf(d.day);
  const kg = isNum(d.weight_kg) ? d.weight_kg : isNum(d.weight_lbs) ? lbToKg(d.weight_lbs) : null;
  if (kg !== null) setMetric(r, d.day, 'weightKg', kg, 'apple_health', at);
  if (isNum(d.lean_mass_lbs)) setMetric(r, d.day, 'leanMassKg', lbToKg(d.lean_mass_lbs), 'apple_health', at);
  if (isNum(d.body_fat_pct)) setMetric(r, d.day, 'bodyFatPct', d.body_fat_pct, 'apple_health', at);
  if (isNum(d.active_kcal)) setMetric(r, d.day, 'activeKcal', d.active_kcal, 'apple_health', at);
  if (isNum(d.resting_hr)) setMetric(r, d.day, 'restingHr', d.resting_hr, 'apple_health', at);
  if (isNum(d.hrv_ms)) setMetric(r, d.day, 'hrvMs', d.hrv_ms, 'apple_health', at);
  inv.counts['health-days'] = (inv.counts['health-days'] ?? 0) + 1;
}

function mapRenphoReading(d: Record<string, unknown>, r: LegacyRecords, inv: Inventory) {
  if (typeof d.date !== 'string') { inv.skipped.push({ what: 'renpho reading', why: 'no date' }); return; }
  const at = noonOf(d.date);
  // Readings the Renpho file itself attributes to Apple Health keep that source.
  const source: Reading['source'] = d.source === 'smart_scale' ? 'scale' : 'apple_health';
  if (isNum(d.weight_lbs)) setMetric(r, d.date, 'weightKg', lbToKg(d.weight_lbs), source, at);
  if (isNum(d.body_fat_pct)) setMetric(r, d.date, 'bodyFatPct', d.body_fat_pct, source, at);
  if (isNum(d.lean_mass_lbs)) setMetric(r, d.date, 'leanMassKg', lbToKg(d.lean_mass_lbs), source, at);
  for (const extra of ['bmi', 'skeletal_muscle_pct', 'subcutaneous_fat_pct']) {
    if (isNum(d[extra])) inv.skipped.push({ what: `${extra} on ${d.date}`, why: 'not modelled in the new app' });
  }
  inv.counts['renpho-readings'] = (inv.counts['renpho-readings'] ?? 0) + 1;
}

function mapAppleWorkout(w: Record<string, unknown>, r: LegacyRecords, inv: Inventory) {
  const start = typeof w.start === 'string' ? parseHaeDate(w.start) : null;
  const day = typeof w.day === 'string' ? w.day : start ? localDay(start.toISOString()) : null;
  if (!day) { inv.skipped.push({ what: 'apple workout', why: 'no date' }); return; }
  const startedAt = start?.toISOString() ?? noonOf(day);
  const dur = isNum(w.duration_sec) ? w.duration_sec : 0;
  const note = [
    isNum(w.active_kcal) ? `${w.active_kcal} kcal` : '', isNum(w.avg_hr) ? `avg HR ${w.avg_hr}` : '', isNum(w.max_hr) ? `max HR ${w.max_hr}` : '',
    typeof w.location === 'string' ? w.location : '', 'via Apple Health',
  ].filter(Boolean).join(' · ');
  r.sessions.push({
    id: `legacy-aw-${String(w.id ?? `${day}-${slug(String(w.name ?? 'workout'))}`)}`,
    day, startedAt, endedAt: new Date(new Date(startedAt).getTime() + dur * 1000).toISOString(),
    name: String(w.name ?? 'Workout'), exercises: [], note,
  });
  inv.counts['apple-workouts'] = (inv.counts['apple-workouts'] ?? 0) + 1;
}

/** The old server's dispatch inbox: food, weight and workout entries. */
export function mapInbox(rows: Record<string, unknown>[]): LegacyResult {
  const res = empty('inbox-jsonl');
  const r = res.records; const inv = res.inventory;
  for (const row of rows) {
    const id = String(row.id ?? '');
    const whenRaw = typeof row.when === 'string' ? row.when : typeof row.received_at === 'string' ? row.received_at : '';
    const whenDate = whenRaw.length === 10 ? null : parseHaeDate(whenRaw);
    const day = whenRaw.length === 10 ? whenRaw : whenDate ? localDay(whenDate.toISOString()) : null;
    const at = whenDate?.toISOString() ?? (day ? noonOf(day) : null);
    if (!day || !at) { inv.skipped.push({ what: `inbox ${row.type} ${id}`, why: 'no usable date' }); continue; }

    if (row.type === 'food' && row.food && typeof row.food === 'object') {
      const f = row.food as Record<string, unknown>;
      const slot = OLD_SLOTS[String(f.meal ?? 'snack').toLowerCase()] ?? 'snack';
      const item = oldFoodItem({ food: f.name, cal: f.cal, p: f.protein_g, c: f.carbs_g, f: f.fat_g, amount: f.amount });
      r.foodLogs.push({ id: `legacy-inbox-${id}`, day, at, slot, items: [item], ...(typeof f.notes === 'string' && f.notes ? { note: f.notes } : {}) });
      inv.counts.meals = (inv.counts.meals ?? 0) + 1;
    } else if (row.type === 'weight' && row.weight && typeof row.weight === 'object') {
      const w = row.weight as Record<string, unknown>;
      if (!isNum(w.lbs)) { inv.skipped.push({ what: `inbox weight ${id}`, why: 'no lbs value' }); continue; }
      const source: Reading['source'] = /renpho|scale/i.test(String(w.notes ?? '')) ? 'scale' : 'manual';
      setMetric(r, day, 'weightKg', lbToKg(w.lbs), source, at);
      inv.counts.weights = (inv.counts.weights ?? 0) + 1;
    } else if (row.type === 'workout' && row.workout && typeof row.workout === 'object') {
      const w = row.workout as Record<string, unknown>;
      const mins = isNum(w.duration_min) ? w.duration_min : 0;
      const note = [typeof w.intensity === 'string' ? `intensity ${w.intensity}` : '', isNum(w.calories_burned) ? `${w.calories_burned} kcal (estimated)` : '', typeof w.notes === 'string' ? w.notes : ''].filter(Boolean).join(' · ');
      r.sessions.push({
        id: `legacy-inbox-${id}`, day, startedAt: at,
        endedAt: new Date(new Date(at).getTime() + mins * 60_000).toISOString(),
        name: String(w.activity ?? 'Workout'), exercises: [], note,
      });
      inv.counts.workouts = (inv.counts.workouts ?? 0) + 1;
    } else {
      inv.skipped.push({ what: `inbox ${String(row.type)} ${id}`, why: 'unknown entry type' });
    }
  }
  return res;
}

export function mapOuraLedger(rows: Record<string, unknown>[]): LegacyResult {
  const res = empty('oura-ledger');
  for (const row of rows) {
    const at = typeof row.pulled_at === 'string' ? (parseHaeDate(row.pulled_at)?.toISOString() ?? noonOf(String(row.day))) : noonOf(String(row.day));
    const parsed = parseLedgerRow(row, at);
    if (!parsed) { res.inventory.skipped.push({ what: `oura ${String(row.day)}`, why: 'no numeric fields (nap-only or incomplete day)' }); continue; }
    res.records.metrics.set(parsed.day, { ...(res.records.metrics.get(parsed.day) ?? {}), ...parsed.fields });
    res.inventory.counts['oura-days'] = (res.inventory.counts['oura-days'] ?? 0) + 1;
  }
  return res;
}

export function mapAppleHealthRows(rows: Record<string, unknown>[]): LegacyResult {
  const res = empty('apple-health');
  for (const d of rows) mapHealthRow(d, res.records, res.inventory);
  return res;
}

export function mapAppleWorkouts(rows: Record<string, unknown>[]): LegacyResult {
  const res = empty('apple-workouts');
  for (const w of rows) mapAppleWorkout(w, res.records, res.inventory);
  return res;
}

export function mapRenpho(file: { readings?: Record<string, unknown>[] }): LegacyResult {
  const res = empty('renpho');
  for (const d of Array.isArray(file.readings) ? file.readings : []) mapRenphoReading(d, res.records, res.inventory);
  return res;
}

/** Route detected content to its mapper. `reta-export` is not handled here. */
export function mapLegacy(det: Detected): LegacyResult | null {
  switch (det.kind) {
    case 'old-state': return mapOldState(det.data as Record<string, unknown>);
    case 'inbox-jsonl': return mapInbox(det.data as Record<string, unknown>[]);
    case 'oura-ledger': return mapOuraLedger(det.data as Record<string, unknown>[]);
    case 'apple-health': return mapAppleHealthRows(det.data as Record<string, unknown>[]);
    case 'apple-workouts': return mapAppleWorkouts(det.data as Record<string, unknown>[]);
    case 'renpho': return mapRenpho(det.data as { readings?: Record<string, unknown>[] });
    default: return null;
  }
}

/**
 * Combine results from several sources. Food logs that describe the same
 * meal twice (the old app pulled inbox entries into its own day log) are
 * collapsed: an inbox entry is dropped when a day-log meal on the same day
 * already lists an item with the same name.
 */
export function combine(results: LegacyResult[]): LegacyResult {
  const out = empty('old-state');
  out.inventory.kind = 'old-state';
  const seenFood = new Map<string, Set<string>>(); // day -> item names from day logs
  const dayLogs = results.flatMap((r) => r.records.foodLogs.filter((f) => f.id.startsWith('legacy-meal-')));
  for (const f of dayLogs) {
    const set = seenFood.get(f.day) ?? new Set();
    for (const i of f.items) set.add(i.name.toLowerCase().trim());
    seenFood.set(f.day, set);
  }
  let deduped = 0;
  for (const r of results) {
    for (const [day, fields] of r.records.metrics) {
      out.records.metrics.set(day, { ...(out.records.metrics.get(day) ?? {}), ...fields });
    }
    for (const f of r.records.foodLogs) {
      if (f.id.startsWith('legacy-inbox-') && f.items.some((i) => seenFood.get(f.day)?.has(i.name.toLowerCase().trim()))) { deduped++; continue; }
      out.records.foodLogs.push(f);
    }
    out.records.checkins.push(...r.records.checkins);
    out.records.foods.push(...r.records.foods);
    out.records.doses.push(...r.records.doses);
    out.records.vials.push(...r.records.vials);
    out.records.sessions.push(...r.records.sessions);
    if (r.records.goals) out.records.goals = { ...(out.records.goals ?? {}), ...r.records.goals };
    if (r.records.profile) out.records.profile = { ...(out.records.profile ?? {}), ...r.records.profile };
    Object.assign(out.records.startedOn, r.records.startedOn);
    for (const [k, n] of Object.entries(r.inventory.counts)) out.inventory.counts[k] = (out.inventory.counts[k] ?? 0) + n;
    out.inventory.skipped.push(...r.inventory.skipped);
    out.inventory.warnings.push(...r.inventory.warnings);
  }
  if (deduped) out.inventory.skipped.push({ what: 'inbox meals already present in the day log', why: 'duplicate of a day-log meal', count: deduped });
  // De-duplicate by id across sources (the same workout can arrive via cache and file).
  const uniq = <T extends { id: string }>(xs: T[]) => [...new Map(xs.map((x) => [x.id, x])).values()];
  out.records.foodLogs = uniq(out.records.foodLogs);
  out.records.doses = uniq(out.records.doses);
  out.records.vials = uniq(out.records.vials);
  out.records.sessions = uniq(out.records.sessions);
  out.records.foods = uniq(out.records.foods);
  out.records.checkins = [...new Map(out.records.checkins.map((c) => [c.day, c])).values()];
  return out;
}

// --------------------------------------------------------------------------
// Applying to the database
// --------------------------------------------------------------------------

export interface ApplyReport { written: Record<string, number>; metricsDays: number }

/**
 * Write a mapped result into IndexedDB. Idempotent: ids are deterministic and
 * metric days merge by source precedence, so running it twice changes nothing.
 */
export async function applyLegacy(result: LegacyResult): Promise<ApplyReport> {
  const r = result.records;
  const written: Record<string, number> = {};

  // Compounds referenced by doses/vials must exist; create a minimal one if not.
  const needed = new Set([...r.doses.map((d) => d.compoundId), ...r.vials.map((v) => v.compoundId), ...Object.keys(r.startedOn)]);
  for (const id of needed) {
    const existing = await get('compounds', id);
    if (!existing) {
      const c: Compound = { id, name: id === 'reta' ? 'Retatrutide' : id, route: 'subcutaneous', reconstituted: true, displayUnit: 'mg', color: '#e98862', active: true, createdAt: noonOf('2026-07-13') };
      await put('compounds', c);
    }
  }

  await putMany('doses', r.doses); written.doses = r.doses.length;
  await putMany('vials', r.vials); written.vials = r.vials.length;
  await putMany('foodLogs', r.foodLogs); written.foodLogs = r.foodLogs.length;
  await putMany('sessions', r.sessions); written.sessions = r.sessions.length;
  // Library foods: keep an existing use count if the item is already there.
  for (const f of r.foods) {
    await update('foods', f.id, (cur) => (cur ? { ...f, useCount: Math.max(cur.useCount ?? 0, f.useCount ?? 0), createdAt: cur.createdAt } : f));
  }
  written.foods = r.foods.length;
  for (const c of r.checkins) {
    await update('checkins', c.day, (cur) => ({ ...(cur ?? { day: c.day }), ...c, updatedAt: cur && cur.updatedAt > c.updatedAt ? cur.updatedAt : c.updatedAt }));
  }
  written.checkins = r.checkins.length;

  let metricsDays = 0;
  for (const [day, fields] of r.metrics) {
    await update('metrics', day, (cur) => mergeDay(cur as DailyMetrics | undefined, day, fields));
    metricsDays++;
  }

  // Started-on dates land on the matching protocol, only if not already set.
  for (const [compoundId, day] of Object.entries(r.startedOn)) {
    const protocols = (await getAll('protocols')) as Protocol[];
    for (const p of protocols.filter((x) => x.compoundId === compoundId && !x.startedOn)) {
      await put('protocols', { ...p, startedOn: day });
    }
  }

  const settings = await loadSettings();
  const next = { ...settings };
  if (r.goals) next.goals = { ...settings.goals, ...r.goals };
  if (r.profile) next.profile = { ...(settings.profile ?? {}), ...r.profile };
  if (r.goals || r.profile) await saveSettings(next);

  return { written, metricsDays };
}

/** Import a file of any supported kind. Returns the report for the UI. */
export async function importLegacyText(text: string): Promise<{ inventory: Inventory; report: ApplyReport } | { error: string }> {
  const det = detectLegacy(text);
  if (det.kind === 'unknown') return { error: 'Could not recognise that file. Supported: the previous Reta backup or state, inbox .jsonl, oura-ledger.jsonl, apple_health.jsonl, apple_workouts.jsonl, renpho.json.' };
  if (det.kind === 'reta-export') {
    const v = validateExport(det.data);
    return v.ok ? { error: 'That is a backup from this app: use Restore from a backup instead.' } : { error: v.error };
  }
  const mapped = mapLegacy(det);
  if (!mapped) return { error: 'Nothing recognisable in that file.' };
  const report = await applyLegacy(mapped);
  return { inventory: mapped.inventory, report };
}

void newId;
